import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import DynamicFormEditor from '../components/DynamicFormEditor';

export default function CollectionEventDetail() {
  const { collectionEventId } = useParams();
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formDef, setFormDef] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Filled form values for completed events
  const [filledValues, setFilledValues] = useState(null);
  const [filledLoading, setFilledLoading] = useState(false);
  const [filledError, setFilledError] = useState(null);

  useEffect(() => {
    if (!tokens?.id_token) return;
    setLoading(true);
    fetch(`${apiBase}/collection-events/${collectionEventId}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error('Failed to fetch collection event');
        }
        return res.json();
      })
      .then(data => setEvent(data))
      .catch(err => {
        console.error(err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [tokens, apiBase, collectionEventId]);

  // Fetch the dynamic collection form definition
  useEffect(() => {
    const formId = event?.dynamic_collection_form_id;
    if (!tokens?.id_token || !formId) return;
    setFormLoading(true);
    fetch(`${apiBase}/dynamic-forms/${formId}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch form definition');
        return res.json();
      })
      .then(data => setFormDef(data))
      .catch(err => {
        console.error(err);
        setFormError(err.message);
      })
      .finally(() => setFormLoading(false));
  }, [tokens, apiBase, event]);

  // Handle form submission
  const handleSubmit = (values) => {
    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    const payload = {
      fields: Object.entries(values).map(([fieldId, val]) => ({
        dynamic_form_field_id: fieldId,
        value: val,
      })),
    };
    fetch(`${apiBase}/dynamic-forms/${formDef.id}/fill-form`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens.id_token}`,
      },
      body: JSON.stringify(payload),
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to submit form');
        return res.json();
      })
      .then(() => {
        setSubmitSuccess(true);
      })
      .catch(err => {
        console.error(err);
        setSubmitError(err.message);
      })
      .finally(() => setSubmitLoading(false));
  };
  // Fetch filled form values for completed events
  useEffect(() => {
    if (event?.status === 'Completed' && formDef && tokens?.id_token) {
      setFilledLoading(true);
      setFilledError(null);
      fetch(`${apiBase}/dynamic-forms/${formDef.id}/fill-form`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` },
      })
        .then(res => {
          if (!res.ok) throw new Error('Failed to fetch filled form');
          return res.json();
        })
        .then(data => setFilledValues(data.fields))
        .catch(err => {
          console.error(err);
          setFilledError(err.message);
        })
        .finally(() => setFilledLoading(false));
    }
  }, [event, formDef, tokens, apiBase]);

  if (loading) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container disableGutters sx={{ mt: 2 }}>
        <Typography color="error" variant="h6">
          Error: {error}
        </Typography>
      </Container>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Collection Event {collectionEventId}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardHeader title={`Event ${event.id}`} subheader={`Status: ${event.status}`} />
        <CardContent>
          <Typography variant="body2">
            <strong>Patient:</strong> {event.patient_first_name} {event.patient_last_name}
          </Typography>
          <Typography variant="body2">
            <strong>MRN:</strong> {event.patient_mrn}
          </Typography>
          <Typography variant="body2">
            <strong>Practice:</strong> {event.practice_name}
          </Typography>
          <Typography variant="body2">
            <strong>Created:</strong> {new Date(event.created_at).toLocaleString()}
          </Typography>
          <Typography variant="body2">
            <strong>Updated:</strong> {new Date(event.updated_at).toLocaleString()}
          </Typography>
        </CardContent>
      </Card>
      {/* Dynamic Collection Form or Summary */}
      {formDef && event.status !== 'Completed' && (
        <DynamicFormEditor formDef={formDef} onSubmit={handleSubmit} />
      )}
      {formDef && event.status === 'Completed' && (
        <>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Form Summary
          </Typography>
          {filledLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {filledError && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {filledError}
            </Typography>
          )}
          {filledValues && (
            formDef.groups.map(group => (
              <Box key={group.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {group.name}
                </Typography>
                <List dense>
                  {group.fields.map(field => {
                    const fv = filledValues.find(f => f.dynamic_form_field_id === field.id);
                    const value = fv ? fv.value : '';
                    return (
                      <ListItem key={field.id} disableGutters>
                        <ListItemText primary={field.name} secondary={value} />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            ))
          )}
        </>
      )}
      {submitLoading && event.status !== 'Completed' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {submitError && event.status !== 'Completed' && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {submitError}
        </Typography>
      )}
      {submitSuccess && event.status !== 'Completed' && (
        <Typography color="primary" variant="body2" sx={{ mt: 2 }}>
          Form submitted successfully
        </Typography>
      )}
    </Container>
  );
}